import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ClientsResponse,
  GetClientsByPlatformRequest,
} from "./clientsTypes";
import { createLogger } from "../../utils/logger";

const logger = createLogger("clientsSlice");

interface ClientsState {
  loading: boolean;
  error?: string;
  data?: ClientsResponse;
  /** True once a fetch has been attempted (success or failure) — prevents infinite retry loops */
  hasFetched: boolean;
}

const initialState: ClientsState = {
  loading: false,
  hasFetched: false,
};

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    getClientsByPlatformRequested: (
      state,
      action: PayloadAction<GetClientsByPlatformRequest>,
    ) => {
      state.loading = true;
      state.error = undefined;
      state.data = undefined;
      void action;
    },
    getClientsByPlatformSucceeded: (
      state,
      action: PayloadAction<{ data: ClientsResponse }>,
    ) => {
      state.loading = false;
      state.error = undefined;
      // Ensure data is always an array
      const rawData = action.payload.data;
      if (Array.isArray(rawData)) {
        state.data = rawData;
      } else if (rawData && typeof rawData === "object") {
        // Handle case where API returns { clients: [...] } or similar
        const obj = rawData as Record<string, unknown>;
        state.data =
          (obj.clients as typeof state.data) ||
          (obj.data as typeof state.data) ||
          [];
        logger.warn("API returned non-array clients data:", rawData);
      } else {
        state.data = [];
        logger.warn("API returned invalid clients data:", rawData);
      }
      state.hasFetched = true;
    },
    getClientsByPlatformFailed: (
      state,
      action: PayloadAction<{ error: string }>,
    ) => {
      state.loading = false;
      state.error = action.payload.error;
      state.data = undefined;
      state.hasFetched = true;
    },
    clearClientsByPlatform: (state) => {
      state.loading = false;
      state.error = undefined;
      state.data = undefined;
      state.hasFetched = false;
    },
    setSelectedClient: (
      state,
      action: PayloadAction<{ clientNumber: string }>,
    ) => {
      if (state.data && Array.isArray(state.data)) {
        state.data = state.data.map((client) => ({
          ...client,
          isSelected: client.client_number === action.payload.clientNumber,
        }));
      }
    },
  },
});

export const clientsActions = clientsSlice.actions;
export const clientsReducer = clientsSlice.reducer;
